import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/useAuth";
import "./GuestAuthPopup.css";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function GuestAuthPopup({ isOpen, onClose, onOpenEmailAuth }) {
  const { refresh } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const gisInitialised = useRef(false);

  /* Load GIS script once */
  useEffect(() => {
    if (window.google || document.getElementById("gsi-script")) return;
    const script = document.createElement("script");
    script.id = "gsi-script";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  /* Handle Google One Tap credential → backend → refresh session */
  const handleCredentialResponse = async (response) => {
    setGoogleLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND}/auth/google/one-tap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ credential: response.credential }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Google sign-in failed");
        setGoogleLoading(false);
        return;
      }
      /* Refresh UserContext — user state updates, App.js fires pendingCallback */
      await refresh();
      localStorage.setItem("isLoggedIn", "true");
      window.dispatchEvent(new Event("auth-changed"));
      onClose();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleClick = () => {
    if (!GOOGLE_CLIENT_ID) {
      setError("Google sign-in is not configured.");
      return;
    }
    if (!window.google) {
      // Direct redirect to backend OAuth endpoint if Google SDK is not loaded
      window.location.href = `${BACKEND}/auth/google/login`;
      return;
    }
    setError("");

    // Clear Google One Tap cooldown cookie (g_state) to reset prompt suppression
    try {
      document.cookie = "g_state=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    } catch (e) {
      console.warn("Failed to clear g_state cookie:", e);
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: false, // Don't cancel immediately on clicking outside
      context: "signin",
    });

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        console.warn("One Tap prompt suppressed. Falling back to Google OAuth redirect...", notification.getMomentType());
        // Fallback: Redirect to standard Google OAuth page to ensure the account chooser is shown
        window.location.href = `${BACKEND}/auth/google/login`;
      }
    });
  };

  const handleEmailClick = () => {
    onClose();
    onOpenEmailAuth?.();
  };

  if (!isOpen) return null;

  return (
    <div className="gap-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="gap-card" role="dialog" aria-modal="true" aria-label="Sign in to MarsMate">
        <button className="gap-close" onClick={onClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Brand mark */}
        <div className="gap-brand">
          <div className="gap-brand-icon">M</div>
        </div>

        <h3 className="gap-title">Sign in or create an account</h3>
        <p className="gap-sub">Save your results and access your reports</p>

        {error && <p className="gap-error">{error}</p>}

        {/* Google One Tap trigger */}
        <button
          className="gap-btn gap-btn-google"
          onClick={handleGoogleClick}
          disabled={googleLoading}
        >
          {googleLoading ? (
            <span className="gap-spinner" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
          )}
          Continue with Google
        </button>

        <div className="gap-divider"><span>or</span></div>

        {/* Email → opens existing AuthContainer */}
        <button className="gap-btn gap-btn-email" onClick={handleEmailClick}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
               aria-hidden="true">
            <rect x="2" y="4" width="20" height="16" rx="2"/>
            <path d="M22 7l-10 7L2 7"/>
          </svg>
          Continue with email
        </button>

        <p className="gap-legal">
          By continuing you agree to our{" "}
          <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>{" "}
          &amp;{" "}
          <a href="/terms-conditions" target="_blank" rel="noreferrer">Terms</a>
        </p>
      </div>
    </div>
  );
}
